import { setupClass, makeEnum } from '../Base.js'
import * as ser from '../Common/SerUtil.js'
import { TextAnimator } from './TextAnimator.js'
import { CLIENT_TOOLS } from './Tools.js'
import { ChangerTool } from './ChangerTool.js'
import { gApp } from '../Globals.js'

export const DEBUGGING = true

export let ChatBlockType = makeEnum({
  UserMessage: 'User Message',
  Response: 'Response',
  FieldChangesProposal: 'Field Changes Proposal',
})

export let ChatState = makeEnum({
  Idle: 'Idle',
  Streaming: 'Streaming',
})

const kDefaultActivityLabel = 'Thinking'

// Status shown in the thinking indicator while the assistant runs a client tool.
// The label sticks until more text arrives (or the next tool call), since most
// tools return instantly and the model then spends a while on the result.
const kToolActivityLabels = {
  read_project: 'Reading project',
  propose_changes: 'Preparing changes',
  get_project_errors: 'Checking for errors',
  calculate_system: 'Running calculation',
  check_system_calculation_status: 'Checking calculation',
  get_system_results: 'Reviewing results',
  search_location: 'Searching locations',
}

export class ChatBlock {
  getType() {
    throw new Error('getType() not implemented')
  }

  getText() {
    throw new Error('getText() not implemented')
  }

  getFullText() {
    return this.getText()
  }

  // Role used when this block is serialized into the messages array sent to
  // the API. Default is 'assistant'; user-authored blocks override.
  getRole() {
    return 'assistant'
  }
}

export class UserMessage extends ChatBlock {
  static serInfo = { type: 'UserMessage' }

  init(text) {
    this.text = text
  }

  get serFields() {
    return [ser.staticField('_type', 'UserMessage'), 'text']
  }

  getType() {
    return ChatBlockType.UserMessage
  }

  getRole() {
    return 'user'
  }

  // Display text: file contents (between <<<| and |>>>) replaced with '...'
  getText() {
    return this.text.replace(/<<<\|[\s\S]*?\|>>>/g, '...')
  }

  // Full text including file contents, used when sending to the API
  getFullText() {
    return this.text
  }
}
setupClass(UserMessage)

export class Response extends ChatBlock {
  static serInfo = { type: 'Response' }

  init(text) {
    this.text = text
    this._animator = TextAnimator.create()
    if (text) {
      this._animator.setText(text)
      this._animator.flush()
    }
  }

  get serFields() {
    return [ser.staticField('_type', 'Response'), 'text']
  }

  getType() {
    return ChatBlockType.Response
  }

  getText() {
    return this.text
  }

  appendText(chunk) {
    this.text += chunk
    // Animation disabled for now (looked clunky) — flush immediately so text
    // shows up as soon as it arrives instead of trickling in.
    this._animator.setText(this.text)
    this._animator.flush()
  }

  getSettledText() {
    if (this._animator.fullText !== this.text) {
      this._animator.setText(this.text)
      this._animator.flush()
    }
    return this._animator.getSettledText()
  }

  flushAnimation() {
    this._animator.flush()
  }
}
setupClass(Response)

// Represents a set of field changes proposed by the assistant, pending user accept/reject.
// Once the user decides, the proposal contributes a user-role message to the
// chat history so the model sees the outcome on the next turn.
export class FieldChangesProposal extends ChatBlock {
  static serInfo = { type: 'FieldChangesProposal' }

  init(data) {
    this.changes = data.changes
    // 'pending' | 'accepted' | 'rejected'
    this.status = 'pending'
    // Changes that failed to apply on accept: [{component_path, error}]
    this.applyErrors = []
  }

  getType() { return ChatBlockType.FieldChangesProposal }
  getRole() { return 'user' }
  getText() { return '' }
  getFullText() {
    if (this.status === 'accepted' && this.applyErrors.length) {
      const failures = this.applyErrors.map(e => `${e.component_path ?? '(unknown path)'}: ${e.error}`).join('; ')
      return `[The user accepted the proposed changes, but some could not be applied: ${failures}. The rest were applied.]`
    }
    if (this.status === 'accepted') return '[The user accepted the proposed changes; they have been applied to the project.]'
    if (this.status === 'rejected') return '[The user rejected the proposed changes; nothing was applied.]'
    return ''
  }
}
setupClass(FieldChangesProposal)

export class Assistant {
  init() {
    this.chatBlocks = []
    this.chatState = ChatState.Idle
    this._ws = null
    this._connecting = null
    this._pendingRequest = null
    // Shown in the thinking indicator while a request is in flight.
    this.activityLabel = kDefaultActivityLabel
    // Percent of this period's AI usage allowance left, or null until known.
    this.percentRemaining = null
  }

  // Fetches the current AI usage remaining (as a percent, no dollar amounts).
  // Called on mount so the indicator has a value before the first message is
  // sent; updated live after that from each 'done' message off the socket.
  async refreshUsage() {
    try {
      const response = await gApp.fetchFromServer('get-ai-usage', 'GET', null, { reportErrors: false })
      this.percentRemaining = response.percentRemaining
    } catch (err) {
      console.error('Failed to load AI usage:', err)
    }
  }

  addIntroMessage() {
    if (this.chatBlocks.length > 0) return
    const intro = `👋 Hi! I'm the HeatWise AI. Use me to:

---

💬 **Ask questions**
Ask anything about HeatWise, your results, or HVAC methodology.
*"How do I set the Ventilation Effectiveness field?"*

✏️ **Fill out values**
I can help you fill out your project. Just tell me what to do.
*"Create three walls with R-values A, B, and C."*

📂 **Import data**
Attach a spreadsheet, PDF, or CSV and I'll import the data for you. Supports .xlsx, .csv, .txt, and .pdf.
*"Create components from the attached file."*

🔍 **Troubleshoot issues**
Ask me to explain unexpected results.
*"Why is the cooling load so high for the conference room?"*

---

What can I help you with?`
    const block = Response.create('')
    block._animator.charsPerTick = 2
    block.appendText(intro)
    this.chatBlocks.push(block)
  }

  loadDummyBlocks() {
    const userMsg = (text) => { let b = UserMessage.create(text); return b; }
    const response = (text) => { let b = Response.create(text); return b; }
    this.chatBlocks = [
      userMsg('What is the cooling load for the living room?'),
      response('The living room has a cooling load of 3,200 BTU/hr. This is driven primarily by solar gain through the south-facing windows (1,800 BTU/hr) and internal gains from occupants and lighting (1,400 BTU/hr).'),
      userMsg('How can I reduce it?'),
      response('A few options to reduce the cooling load:\n\n1. Add external shading (overhangs or blinds) to the south windows — could cut solar gain by 40–60%.\n2. Increase wall insulation from R-13 to R-21.\n3. Upgrade to low-e glazing (SHGC ≤ 0.25).\n\nThe biggest impact would come from shading the windows.'),
      userMsg('What U-value should I use for the roof assembly?'),
      response('For a well-insulated residential roof in climate zone 4, a U-value of 0.026 (R-38) is typical and meets ASHRAE 90.1 requirements. Your current roof is set to U-0.047 (R-21), so there is room to improve.'),
    ]
  }

  get serFields() {
    return [];
  }

  getChatBlocks() {
    return this.chatBlocks
  }

  getChatState() {
    return this.chatState
  }

  getActivityLabel() {
    return this.activityLabel
  }

  // Percent of this period's AI usage allowance left (0-100), or null if not
  // yet known (before the first refreshUsage()/assistant response).
  getPercentRemaining() {
    return this.percentRemaining
  }

  // --- WebSocket connection management ---

  _getWsUrl() {
    return gApp.serverEndpoint.replace(/^http/, 'ws') + '/assistant/ws'
  }

  _ensureConnected() {
    if (this._ws?.readyState === WebSocket.OPEN) {
      return Promise.resolve()
    }
    if (this._connecting) {
      return this._connecting
    }
    this._connecting = new Promise((resolve, reject) => {
      const ws = new WebSocket(this._getWsUrl())
      this._ws = ws
      ws.onopen = () => {
        this._connecting = null
        ws.send(JSON.stringify({ type: 'setup', tools: CLIENT_TOOLS }))
        resolve()
      }
      ws.onmessage = (e) => this._handleMessageFromServer(e)
      ws.onerror = () => {
        this._connecting = null
        this._ws = null
        reject(new Error('WebSocket connection failed'))
      }
      ws.onclose = () => {
        this._ws = null
        this._connecting = null
        if (this._pendingRequest) {
          this._pendingRequest.reject(new Error('Connection closed'))
          this._pendingRequest = null
          this.chatState = ChatState.Idle
        }
      }
    })
    return this._connecting
  }

  _send(data) {
    if (this._ws?.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify(data))
    }
  }

  // async: WebSocket's onmessage doesn't await its handler, so this just
  // lets tool_call below await tool.handle() (which may be sync or async)
  // without any extra wrapping.
  // Appends response text for the in-flight request. If another block (e.g. a
  // proposal) was added after the current response block, the text starts a
  // new response block below it so the chat reads top-to-bottom.
  _appendResponseText(text) {
    let last = this.chatBlocks[this.chatBlocks.length - 1]
    if (last?.getType() !== ChatBlockType.Response) {
      last = Response.create('')
      this.chatBlocks.push(last)
    }
    last.appendText(text)
  }

  async _handleMessageFromServer(event) {
    const msg = JSON.parse(event.data)
    const pending = this._pendingRequest

    if (msg.type === 'text_delta') {
      console.log(`Received text delta: `, msg.content);
      this.activityLabel = kDefaultActivityLabel
      if (pending) this._appendResponseText(msg.content)
    } else if (msg.type === 'tool_call') {
      console.log(`Got tool-call: ${msg.name} with input:`, msg.input)
      this.activityLabel = kToolActivityLabels[msg.name] ?? kDefaultActivityLabel
      const tool = CLIENT_TOOLS.find(t => t.name === msg.name)
      let result
      try {
        result = await tool?.handle(msg, this)
      } catch (err) {
        console.error(`Tool handler for ${msg.name} threw:`, err)
        result = `Error: ${err.message}`
      }
      this._send({ type: 'client_response', requestId: msg.requestId, result })
    } else if (msg.type === 'done') {
      if (msg.percentRemaining != null) this.percentRemaining = msg.percentRemaining
      pending?.resolve()
    } else if (msg.type === 'error') {
      pending?.reject(new Error(msg.message))
    }
  }

  // --- Public API ---

  stopRequest() {
    this._send({ type: 'abort' })
    if (this._pendingRequest) {
      this._appendResponseText('\n\n*Stopped*')
      this._pendingRequest.resolve()
      this._pendingRequest = null
    }
  }

  async submitDummyRequest(text) {
    if (this.chatState !== ChatState.Idle) {
      return
    }

    this.chatBlocks.push(UserMessage.create(text))

    const responseBlock = Response.create('')
    this.chatBlocks.push(responseBlock)
    this.activityLabel = kDefaultActivityLabel
    this.chatState = ChatState.Streaming

    const dummyResponses = [
      'The cooling load for your living room is primarily driven by solar gain through the south-facing windows.\n\nTo reduce it, consider:\n\n1. Adding external shading (overhangs or blinds)\n2. Upgrading to low-e glazing (SHGC ≤ 0.25)\n3. Increasing wall insulation from R-13 to R-21\n\nThe biggest impact would come from shading the windows, which could cut solar gain by 40–60%.',
      'For a well-insulated roof in climate zone 4, a U-value of **0.026** (R-38) is typical and meets ASHRAE 90.1 requirements.\n\nYour current roof is set to U-0.047 (R-21), so there is meaningful room to improve efficiency.',
      'Based on the project snapshot, your total system heating load is **42,300 BTU/hr**.\n\nThe main contributors are:\n- Infiltration: 18,200 BTU/hr (43%)\n- Walls: 12,400 BTU/hr (29%)\n- Windows: 8,100 BTU/hr (19%)\n- Roof: 3,600 BTU/hr (9%)',
    ]
    const fullText = dummyResponses[Math.floor(Math.random() * dummyResponses.length)]

    try {
      await new Promise((r) => setTimeout(r, 3000))

      const chunkSize = 50
      for (let i = 0; i < fullText.length; i += chunkSize) {
        responseBlock.appendText(fullText.slice(i, i + chunkSize))
        await new Promise((r) => setTimeout(r, 200))
      }
    } catch (err) {
      console.error('Dummy request failed:', err)
      responseBlock.appendText(`Error: ${err.message}`)
    } finally {
      this.chatState = ChatState.Idle
      responseBlock.flushAnimation()
    }
  }

  // Build a messages array from chatBlocks, dropping oldest turns until the
  // total character count is under maxChars. Always keeps the latest user message.
  // Consecutive same-role messages are merged because the Anthropic API requires
  // alternating user/assistant roles (e.g. a resolved FieldChangesProposal + new
  // UserMessage both contribute user-role text).
  buildMessages(maxChars = 40000) {
    // A proposal's accept/reject outcome is moved to the end of its turn: the
    // user decides after the whole response, even though the proposal block is
    // displayed in the middle of it (with response text before and after).
    const ordered = []
    let deferredProposals = []
    for (const block of this.chatBlocks) {
      if (block.getType() === ChatBlockType.FieldChangesProposal) {
        deferredProposals.push(block)
        continue
      }
      if (block.getType() === ChatBlockType.UserMessage) {
        ordered.push(...deferredProposals)
        deferredProposals = []
      }
      ordered.push(block)
    }
    ordered.push(...deferredProposals)

    const raw = ordered
      .filter(b => b.getFullText())
      .map(b => ({ role: b.getRole(), content: b.getFullText() }))

    const all = []
    for (const msg of raw) {
      const last = all[all.length - 1]
      if (last && last.role === msg.role) {
        last.content += '\n\n' + msg.content
      } else {
        all.push({ ...msg })
      }
    }

    let totalChars = all.reduce((sum, m) => sum + m.content.length, 0)
    let start = 0
    while (totalChars > maxChars && start < all.length - 1) {
      totalChars -= all[start].content.length
      start++
    }
    while (start < all.length - 1 && all[start].role !== 'user') {
      totalChars -= all[start].content.length
      start++
    }
    return all.slice(start)
  }

  async submitRequest(text) {
    if (this.chatState !== ChatState.Idle) return

    this.chatBlocks.push(UserMessage.create(text))
    const messages = this.buildMessages()

    this.activityLabel = kDefaultActivityLabel
    this.chatState = ChatState.Streaming

    try {
      console.log(`Connecting to assistant WebSocket at ${this._getWsUrl()}...`)
      await this._ensureConnected()
      console.log(`Connected! Sending request...`)
      const idToken = await gApp.auth.currentUser.getIdToken()

      await new Promise((resolve, reject) => {
        this._pendingRequest = { resolve, reject, messages }
        // projectType lets the server tailor the system prompt (Commercial vs
        // Residential projects have different components and tabs).
        const projectType = gApp.proj().getProjectType()
        this._send({ type: 'user_message', idToken, messages, projectType })
      })
    } catch (err) {
      if (err.message !== 'Connection closed') {
        console.error('Assistant request failed:', err)
        this._appendResponseText(`Error: ${err.message}`)
      }
    } finally {
      this._pendingRequest = null
      this.chatState = ChatState.Idle
    }
  }

  // Called when the user accepts or rejects a field changes proposal.
  // The server has already received an immediate placeholder result for the
  // tool call, so we only update local state here. The decision is recorded
  // on the proposal block and contributes to the chat history that gets sent
  // along with the next user message.
  async resolveProposal(proposal, accepted) {
    if (proposal.status !== 'pending') return
    proposal.status = accepted ? 'accepted' : 'rejected'
    if (accepted) {
      const errors = await ChangerTool.applyChanges(gApp.proj(), proposal.changes)
      if (errors.length) {
        console.warn('Some field changes could not be applied:', errors)
        proposal.applyErrors = errors
      }
    }
  }
}
setupClass(Assistant)
